import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Button, IconButton, Tooltip, Typography, } from "@mui/material";
import { ErrorMessage, Formik } from "formik";
import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import { formatForNetDates } from "../../../../../../util/datetime";
import FormikDatePicker from "../../../../../commons/formik/FormikDatePicker";
import { tableConfig, tableStyles } from "../../../../../commons/table/tableConfig";
import TitleContainer from "../../../../../commons/table/utilsTable/TitleWithFetchData";
import { StyledSiniestralidad } from "../../../../../styled/Siniestros";

const validationSchema = Yup.object().shape({
  fechaDesde: Yup.date()
    .nullable()
    .typeError("Fecha inválida")
    .test({
      name: "fechaDesde",
      message: "La fecha desde debe ser menor o igual a la fecha hasta",
      test: function (value) {
        if (!value || !this.parent.fechaHasta) {
          return true;
        }
        return value <= this.parent.fechaHasta;
      },
    }),

  fechaHasta: Yup.date()
    .nullable()
    .typeError("Fecha inválida")
    .test({
      name: "fechaHasta",
      message: "La fecha hasta debe ser mayor o igual a la fecha desde",
      test: function (value) {
        if (!value || !this.parent.fechaDesde) {
          return true;
        }
        return value >= this.parent.fechaDesde;
      },
    }),
});

const DocumentacionMedicaTable = ({
  data,
  fetchData,
  loading,
  initialValues,
  getInformesMedicos,
  fechaDesde,
  fechaHasta,
  handleFilter,
  fechaProximoControl,
}) => {

  const esUsuarioMedico = useSelector(
    (state) => state.auth.data?.operacionRol?.esMedico
  );

  const [filterModel, setFilterModel] = useState(initialValues);
  const [tableData, setTableData] = useState([]);

  const columns = useMemo(() => [
    {
      header: "Secuencia del Siniestro",
      accessorKey: "nroSecuencia",
      width: "10%",
    },
    {
      header: "Fecha Novedad",
      accessorKey: "fechaNovedad",
      width: "10%",
      Cell: ({ cell }) => formatForNetDates(`${cell.getValue()}`),
    },
    {
      header: "Prestador",
      accessorKey: "prestador",
      width: "10%",
    },
    {
      header: "Documentación Registrada",
      accessorKey: "documentacion",
      width: "10%",
    },
  ]);

  const exportCvs = () => {
    const test = [
      [
        "Secuencia del Siniestro",
        "Fecha novedad",
        "Prestador",
        "Documentación",
      ],
      ...tableData.map((column) => [
        column.nroSecuencia ? `${column.nroSecuencia}` : "",
        column.fechaNovedad ? `${column.fechaNovedad}` : "",
        column.prestador ? `${column.prestador}` : "",
        column.documentacion ? `${column.documentacion}` : "",
      ]),
    ];
    const encoding = "data:text/csv;charset=utf-8,%EF%BB%BF";
    let csvContent = "Documentacion medica,\n";
    csvContent += `${test.map((e) => e.join(",")).join("\n")}`;
    const link = document.createElement("a");
    link.setAttribute("href", encoding + escape(csvContent));
    link.setAttribute("download", `Siniestros.csv`);

    document.body.appendChild(link); // Required for FF

    link.click();
  }

  useEffect(() => {
    if (data.length) {
      const sortedData = [...data].sort(
        (a, b) => a.nroSecuencia - b.nroSecuencia
      );
      setTableData(sortedData);
    } else {
      setTableData([]);
    }
  }, [data]);

  useEffect(() => {
    if (fechaDesde && fechaHasta) {
      setFilterModel({
        ...initialValues,
        fechaDesde: fechaDesde,
        fechaHasta: fechaHasta,
      });
    } else {
      setFilterModel(initialValues);
    }
  }, [fechaDesde, fechaHasta]);

  return (
    <MaterialReactTable
      {...tableConfig}
      columns={columns}
      data={tableData.map((e) => {
        e.nroSecuencia = e.nroSecuencia.toString();
        return e;
      })}
      layoutMode="grid"
      state={{ isLoading: loading }}
      localization={MRT_Localization_ES}
      initialState={{ showColumnFilters: true }}
      style={{ ...tableStyles.container }}
      defaultColumn={{
        enableColumnActions: false,
        minSize: 100,
      }}
      renderTopToolbar={(props) => (
        <>
          <StyledSiniestralidad.TableToolbarContainer style={{ margin: "0px 30px" }}>
            <TitleContainer
              title="Documentación"
              noRefresh={false}
              fetchData={fetchData}
            />
            <Tooltip title="Exportar" >
              <IconButton onClick={exportCvs}  >
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
          </StyledSiniestralidad.TableToolbarContainer>
          <StyledSiniestralidad.TableToolbarContainer>
            <Formik
              initialValues={filterModel}
              validationSchema={validationSchema}
              onSubmit={handleFilter}
            >
              {({ values }) => (
                <StyledSiniestralidad.TableFilterContainer>
                  <StyledSiniestralidad.TableFilterItem>
                    <FormikDatePicker
                      name="fechaDesde"
                      label="Fecha desde"
                      ampm="false"
                      format="dd/MM/yyyy"
                      variant="inline"
                      FormHelperTextProps={{
                        style: {
                          display: "none",
                        },
                      }}
                    />
                    {values.fechaDesde && values.fechaHasta ? (
                      <Typography>
                        <ErrorMessage
                          name="fechaDesde"
                        />
                      </Typography>
                    ) : null}
                  </StyledSiniestralidad.TableFilterItem>
                  <StyledSiniestralidad.TableFilterItem>
                    <FormikDatePicker
                      name="fechaHasta"
                      label="Fecha hasta"
                      ampm="false"
                      format="dd/MM/yyyy"
                      variant="inline"
                      FormHelperTextProps={{
                        style: {
                          display: "none",
                        },
                      }}
                    />
                    {values.fechaHasta && values.fechaHasta ? (
                      <Typography>
                        <ErrorMessage
                          name="fechaHasta"
                        />
                      </Typography>
                    ) : null}
                  </StyledSiniestralidad.TableFilterItem>
                  <StyledSiniestralidad.ButtonContainer>
                    <Button
                      color="primary"
                      variant="contained"
                      type="submit"
                    >
                      Buscar
                    </Button>
                  </StyledSiniestralidad.ButtonContainer>
                </StyledSiniestralidad.TableFilterContainer>
              )}
            </Formik>
            <span>
              <StyledSiniestralidad.InformesMedicos variant="caption" noWrap color="textPrimary">
                Fecha Próximo Control:
              </StyledSiniestralidad.InformesMedicos>
              <StyledSiniestralidad.InformesMedicos variant="caption" noWrap color="textSecondary">
                {fechaProximoControl ? moment(fechaProximoControl).format("DD-MM-YYYY") : "Sin Próximo Control"}
              </StyledSiniestralidad.InformesMedicos>
            </span>
            <Button
              color="secondary"
              variant="contained"
              sx={{ margin: "15px 15px" }}
              disabled={!esUsuarioMedico}
              onClick={getInformesMedicos}
            >
              Informes Médicos
            </Button>
          </StyledSiniestralidad.TableToolbarContainer>
        </>
      )}
    />
  );
};

export default DocumentacionMedicaTable;
